import SimpleDate from 'app/common/classes/simple-date'
import { canMailingTo } from 'app/common/ui-next/_filter-compilations/mailing'
import { DetailsGroup } from 'app/components/Reports/_Subsidies/_common/useReportDetailsGroup'
import {
  DetailedSubsidyClassType,
  DetailedSubsidyFilterKey as ID,
} from 'app/components/Reports/_Subsidies/Detailed/ReportFilter/filterItems/types'

type ApiClassType = 'klasses' | 'day_cares' | 'camp'

export type ApiSubsidiesDetailedParams = {
  report_type: 'subsidies_detailed'
  params: {
    from: string
    to: string
    limits: {
      school_ids: number[]
      schoolyear_id?: number
      chains?: string[]
      parallels?: number[]
      klass_ids?: number[]
      student_ids?: number[]
      employee_ids?: number[]
      allowances?: string[]
      sums?: number[]
      complexes?: number[]
      order_type: string
      view_type: string
      group_type?: ApiClassType
    }
    details: boolean
    details_group?: DetailsGroup
  }
  options:
    | Record<string, never>
    | {
        send_to_email: string | null
        additional: Record<string, never>
      }
}

const formDateFormat = 'DD.MM.YYYY'
const apiDateFormat = 'YYYY-MM-DD'
const classTypesForApi: Record<DetailedSubsidyClassType, ApiClassType> = {
  [DetailedSubsidyClassType.Common]: 'klasses',
  [DetailedSubsidyClassType.DayCares]: 'day_cares',
  [DetailedSubsidyClassType.Camp]: 'camp',
}
const all = (formData: FormData, id: string) => formData.getAll(id).map(String)
const numbers = (values: string[]) => values.map((value) => Number(value))
const apiDate = (value: string) =>
  new SimpleDate(value, formDateFormat).getStringFormatted(apiDateFormat)

export const convertParams = (formData: FormData): [ApiSubsidiesDetailedParams, null] => {
  const periodFrom = formData.get(`${ID.Period}From`) as string
  const periodTo = formData.get(`${ID.Period}To`) as string
  const details = !!formData.get(ID.Details)
  const classType = formData.get(ID.ClassType) as DetailedSubsidyClassType | null
  const orgType = formData.get(ID.OrgTypes) as string | null
  const orgs = all(formData, ID.Orgs)
  const classes = all(formData, ID.Classes)
  const orderType = (formData.get(ID.SubsidyType) as string) || 'all'
  const year = formData.get(ID.SchoolYear) as string | null
  const sumsAndKits = all(formData, ID.SumsAndKits)
  const sums = sumsAndKits
    .filter((id) => id.startsWith('sum_'))
    .map((id) => parseFloat(id.slice(4)))
  const complexes = numbers(sumsAndKits.filter((id) => !id.startsWith('sum_')))
  const email = canMailingTo(formData)
  const detailsGroup = formData.get(ID.Group) as DetailsGroup | null
  const groupType = classType ? classTypesForApi[classType] : undefined
  const singleOrg = orgs.length === 1
  const educationLevelsActive =
    orgType === 'school' &&
    singleOrg &&
    !!year &&
    classType === DetailedSubsidyClassType.Common
  const classesActive =
    singleOrg &&
    !!year &&
    orgType !== 'enterprise' &&
    (orgType !== 'school' || classType === DetailedSubsidyClassType.Common)
  const studentsActive = singleOrg && classes.length === 1
  const employeesActive = singleOrg && orgType === 'enterprise'
  const sumsAndKitsActive = singleOrg

  return [
    {
      report_type: 'subsidies_detailed',
      params: {
        from: apiDate(periodFrom),
        to: apiDate(periodTo),
        limits: {
          school_ids: numbers(orgs),
          ...(year ? { schoolyear_id: Number(year) } : {}),
          ...(educationLevelsActive
            ? { chains: all(formData, ID.EducationLevels) }
            : {}),
          ...(formData.has(ID.Parallels)
            ? { parallels: numbers(all(formData, ID.Parallels)) }
            : {}),
          ...(classesActive ? { klass_ids: numbers(classes) } : {}),
          ...(studentsActive
            ? { student_ids: numbers(all(formData, ID.Students)) }
            : {}),
          ...(employeesActive
            ? { employee_ids: numbers(all(formData, ID.Employees)) }
            : {}),
          ...(orderType === 'personal' ? { allowances: all(formData, ID.Allowances) } : {}),
          ...(sumsAndKitsActive ? { sums, complexes } : {}),
          order_type: orderType,
          view_type: (formData.get(ID.ViewType) as string) || 'sum',
          ...(groupType ? { group_type: groupType } : {}),
        },
        details,
        ...(details && detailsGroup ? { details_group: detailsGroup } : {}),
      },
      options: details ? { send_to_email: email, additional: {} } : {},
    },
    null,
  ]
}
